/* ============================================
   useApi Hook — Fetch-based API client
   Replaces useSupabase for all data operations
   ============================================ */

import { useCallback, useMemo, useRef } from 'react'
import { useAuth } from './useAuth'
import { cerrarSesionPorExpiracion, esSesionVencida } from '@/lib/sesion'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: unknown
  headers?: Record<string, string>
}

export function useApi() {
  const { token } = useAuth()
  // Use ref so callbacks don't change when token changes
  const tokenRef = useRef(token)
  tokenRef.current = token

  const request = useCallback(
    async <T = unknown>(path: string, options: ApiOptions = {}): Promise<T> => {
      const { method = 'GET', body, headers: extraHeaders = {} } = options

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...extraHeaders,
      }

      if (tokenRef.current) {
        headers['Authorization'] = `Bearer ${tokenRef.current}`
      }

      const res = await fetch(`${API_URL}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      })

      if (!res.ok) {
        // Sesión vencida: al login, no a un error genérico por pantalla.
        if (esSesionVencida(res.status)) {
          cerrarSesionPorExpiracion()
          throw new Error('Tu sesion expiro')
        }
        const error = await res.json().catch(() => ({ error: 'Error de red' }))
        throw new Error(error.error || `HTTP ${res.status}`)
      }

      return res.json()
    },
    []
  )

  const get = useCallback(
    <T = unknown>(path: string) => request<T>(path),
    [request]
  )

  const post = useCallback(
    <T = unknown>(path: string, body: unknown) => request<T>(path, { method: 'POST', body }),
    [request]
  )

  const put = useCallback(
    <T = unknown>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }),
    [request]
  )

  const del = useCallback(
    <T = unknown>(path: string) => request<T>(path, { method: 'DELETE' }),
    [request]
  )

  const upload = useCallback(
    async <T = unknown>(path: string, formData: FormData): Promise<T> => {
      const headers: Record<string, string> = {}
      if (tokenRef.current) {
        headers['Authorization'] = `Bearer ${tokenRef.current}`
      }

      const res = await fetch(`${API_URL}${path}`, {
        method: 'POST',
        headers,
        body: formData,
      })

      if (!res.ok) {
        // Sesión vencida: al login, no a un error genérico por pantalla.
        if (esSesionVencida(res.status)) {
          cerrarSesionPorExpiracion()
          throw new Error('Tu sesion expiro')
        }
        const error = await res.json().catch(() => ({ error: 'Error de red' }))
        throw new Error(error.error || `HTTP ${res.status}`)
      }

      return res.json()
    },
    []
  )

  /*
    El objeto va memoizado, no solo sus funciones. Cada `useCallback` de arriba ya era
    estable, pero el literal que los envuelve nacía de nuevo en cada render, así que
    `api` cambiaba de identidad siempre. Un componente que lo ponga en las dependencias
    de un efecto entra en bucle infinito: render → `api` nuevo → efecto → setState →
    render. Pasó de verdad (nov-2026) con la carga de notas del chat, a 23 peticiones
    por segundo desde una sola pestaña: el navegador llegó a su tope de conexiones y
    todo lo demás quedó en cola, así que los nombres de los contactos no cargaban y
    hacer clic en otra conversación no abría nada.
  */
  return useMemo(
    () => ({ get, post, put, del, upload, request }),
    [get, post, put, del, upload, request],
  )
}

